import { camelCase, keys, toPairs, uniqueId } from 'lodash';
import { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Accordion,
  AccordionContent,
  AccordionProvider,
  AccordionTitle,
  Badge,
  Banner,
  Card,
  DatePicker,
  Footer,
  FormCheckbox,
  FormInput,
  FormSelect,
  FormTextArea,
  FormToggle,
  Icon,
  InfiniteScroll,
  Loader,
  Modal,
  RadioGroup,
  ReactTable,
  Skeleton,
  Slide,
  Slider,
  Stepper,
  SwitchButtons,
  TabPanel,
  Tabs,
  TabsProvider,
  Tooltip
} from 'components';
import { travelPurchaseSteps } from 'constants';
import { addUuid } from 'helpers';
import { useSafeState, useUser } from 'hooks';
import { appService, copyToClipboard, faIconsList } from 'services';
import { resetFooterBanner, setFooterBanner } from 'store';

import * as customIconsList from 'assets/img/icons';
import styles from 'assets/styles/styles.json';

const docLink = (label, link) => {
  return (
    <a className="flex items-baseline gap-2 w-fit" href={link} target="_blank" rel="noopener noreferrer">
      <span>{label}</span>
      <Icon name="external-link" iconStyle="fad" />
    </a>
  );
};

const SkipLinks = () => {
  const links = [
    { name: 'Accordions', href: '#accordions' },
    { name: 'Badges', href: '#badges' },
    { name: 'Banners', href: '#banners' },
    { name: 'Buttons', href: '#buttons' },
    { name: 'Cards', href: '#cards' },
    { name: 'Colors', href: '#colors' },
    { name: 'Footer', href: '#footer' },
    { name: 'Forms', href: '#forms' },
    { name: 'Icons', href: '#icons' },
    { name: 'Loaders & Skeletons', href: '#loaders' },
    { name: 'Modals', href: '#modals' },
    { name: 'Scrolls', href: '#scrolls' },
    { name: 'Sliders', href: '#sliders' },
    { name: 'Stepper', href: '#stepper' },
    { name: 'Tabs', href: '#tabs' },
    { name: 'Table', href: '#table' },
    { name: 'Toasters', href: '#toasters' },
    { name: 'Tooltips', href: '#tooltips' },
    { name: 'Typography', href: '#typography' }
  ];
  return (
    <aside>
      <ul>
        {links.map((link) => (
          <li key={`link-${link.name}`} className="mb-2">
            <a href={link.href} className="nav-item">
              {link.name}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
};

const StepperComponent = () => {
  const [currentStep, setCurrentStep] = useSafeState(2);

  const handleStepClick = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      console.log('previous step');
    } else {
      console.log('you are in the first step');
    }
  };

  return (
    <>
      <h2 id="stepper" className="h2 underlined-blue mb-4">
        Stepper
      </h2>
      <Stepper steps={travelPurchaseSteps} onStepClick={handleStepClick} className="pb-4" />
    </>
  );
};

const BannerComponents = () => {
  return (
    <>
      <h2 id="banners" className="h2 underlined-blue mb-4">
        Banners
      </h2>

      <Banner icon={{ name: 'circle-info', style: 'fad' }} type="info" className="my-6 lg:w-3/4">
        Velit fugit porro nulla adipisci facilis harum perferendis
      </Banner>

      <Banner icon={{ name: 'circle-info', style: 'fad' }} type="success" className="my-6 lg:w-3/4">
        Velit fugit porro nulla adipisci facilis harum perferendis
      </Banner>

      <Banner icon={{ name: 'circle-info', style: 'fad' }} type="warning" className="my-6 lg:w-3/4">
        Velit fugit porro nulla adipisci facilis harum perferendis
      </Banner>

      <Banner icon={{ name: 'circle-info', style: 'fad' }} type="error" className="my-6 lg:w-3/4">
        Velit fugit porro nulla adipisci facilis harum perferendis
      </Banner>
    </>
  );
};

const Colors = () => {
  return (
    <div className="mb-12">
      <h2 id="colors" className="h2 underlined-blue mb-4">
        Colors
      </h2>

      {toPairs(styles.colors).map(([name, color]) => (
        <div key={`color-${name}`}>
          <h3 className="h3 my-4">{name}</h3>

          <div className="flex flex-wrap gap-3">
            {toPairs(color).map(([shade]) => {
              return (
                <div
                  key={`color-${name}-${shade}`}
                  className={`flex items-end w-32 h-32 rounded-md overflow-hidden shadow-md bg-${name}-${shade}`}
                >
                  <div className="bg-white w-full p-2 text-center">
                    <span className="block">{`bg-${name}-${shade}`}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const Typography = () => {
  const titleFont = appService.isTouloisirs() ? 'Uni Sans' : 'Aqua Grotesque';
  const textFont = appService.isTouloisirs() ? 'Uni Sans' : 'Avenir';

  return (
    <div className="mb-12">
      <h2 id="typography" className="h2 underlined-blue mb-4">
        Typography
      </h2>

      <section>
        <h1 className="h1 mb-4">h1 - {titleFont} - Réservez votre hôtel</h1>
        <h2 className="h2 mb-4">h2 - {titleFont} - Réservez votre hôtel</h2>
        <h3 className="h3 mb-4">h3 - {titleFont} - Réservez votre hôtel</h3>
        <p className="mb-4 mt-12">Text Normal - {textFont} - Regular - Réservez votre hôtel</p>
        <p className="mb-4 uppercase">Text Uppercase - {textFont} - Regular - Réservez votre hôtel</p>
        <p className="mb-4 italic">Text Normal - {textFont} - Regular Italic - Réservez votre hôtel</p>
        <p className="font-bold mb-4">Text Normal - {textFont} - Bold - Réservez votre hôtel</p>
        <p className="text-sm">Text small - {textFont} - Regular - Réservez votre hôtel</p>
      </section>
    </div>
  );
};

const Icons = () => {
  const iconsList = {
    custom: Object.keys(customIconsList),
    library: faIconsList
  };

  const iconButtonClass =
    'shadow-input bg-white p-2 rounded-lg flex flex-col items-center justify-center gap-1 hover:bg-gray-100';

  return (
    <div className="mb-12">
      <h2 id="icons" className="h2 underlined-blue mb-4">
        Icons
      </h2>

      <h3 className="h3 mb-2">Font Awesome Icons</h3>

      <div className="mb-2">
        {docLink('Font Awesome Icons', 'https://fontawesome.com/search?m=free&s=solid')}
        <br />
        {docLink('Font Awesome with React', 'https://fontawesome.com/v5/docs/web/use-with/react')}
      </div>

      <Banner icon={{ name: 'circle-info', style: 'fad' }} type="info" className="my-6 w-fit">
        Tip: You can click on any icon to copy its name
      </Banner>

      <div className="grid grid-cols-12 gap-6">
        {iconsList.library &&
          keys(iconsList.library).map((lib) => (
            <div key={`icon-group-${lib}`} className="col-span-12 lg:col-span-6">
              <p className="font-bold text-lg mb-4 text-primary-500">{lib === 'fas' ? 'Solid' : 'Duotone'}</p>

              <div className="grid grid-cols-4 gap-4">
                {keys(iconsList.library[lib]).map((icon) => (
                  <button
                    className={iconButtonClass}
                    key={icon}
                    type="button"
                    onClick={() => copyToClipboard(lib !== 'fas' ? `['${lib}', '${icon}']` : icon)}
                    title="copy icon name"
                  >
                    <Icon name={icon} iconStyle={lib} libraryIconProps={{ size: 'lg' }} />
                    <span className="text-sm word-break-all">{icon}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
      </div>

      <h3 className="mt-6 mb-2 h3">Custom Icons</h3>

      <div className="grid grid-cols-12 gap-6">
        {iconsList.custom &&
          iconsList.custom.map((icon) => (
            <button
              key={icon}
              type="button"
              className={iconButtonClass}
              onClick={() => copyToClipboard(icon)}
              title="copy icon name"
            >
              <Icon name={icon} classes="w-8" />
              <p className="mt-1">{camelCase(icon)}</p>
            </button>
          ))}
      </div>
    </div>
  );
};

const Buttons = () => {
  const switchButtons = [
    {
      name: 'button1',
      labelKey: 'form:firstname',
      iconName: 'pen',
      disabled: false
    },
    {
      name: 'button2',
      labelKey: 'form:lastname',
      iconName: 'lock',
      disabled: false
    }
  ];
  const [currentSwitchButton, setCurrentSwitchButton] = useSafeState(switchButtons[0].name);

  return (
    <div className="mb-12">
      <h2 id="buttons" className="h2 underlined-blue mb-4">
        Buttons
      </h2>

      <div className="md:grid md:grid-cols-12 md:gap-6">
        <div className="md:col-span-5">
          <div className="flex flex-wrap items-center gap-4">
            <button type="button" className="btn">
              Button Standard
            </button>
            <button type="button" className="btn outlined">
              Button Standard
            </button>
            <button type="button" className="btn" disabled>
              Button Standard
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 my-4">
            <button type="button" className="btn large">
              Button Large
            </button>
            <button type="button" className="btn large outlined">
              Button Large
            </button>
            <button type="button" className="btn large" disabled>
              Button Large
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 my-4">
            <button type="button" className="btn small">
              Button small
            </button>
            <button type="button" className="btn small outlined">
              Button small
            </button>
            <button type="button" className="btn small" disabled>
              Button small
            </button>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-4 my-4">
            <button type="button" className="btn action">
              <Icon name="pen" />
            </button>
            <button type="button" className="btn action">
              <Icon name="trash-alt" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 my-4">
            <button type="button" className="btn control">
              <Icon name="angle-left" />
            </button>
            <button type="button" className="btn control outlined">
              <Icon name="angle-right" />
            </button>
          </div>
        </div>

        <div className="md:col-span-5">
          <SwitchButtons
            buttons={switchButtons}
            activeButton={currentSwitchButton}
            onSelect={(b) => setCurrentSwitchButton(b.name)}
          />
        </div>
      </div>
    </div>
  );
};

const Badges = () => {
  return (
    <div className="mb-12">
      <h2 id="badges" className="h2 underlined-blue mb-4">
        Badges
      </h2>

      <div className="flex flex-wrap gap-4">
        <Badge color="green">Green</Badge>
        <Badge color="success">Success</Badge>
        <Badge color="yellow">Yellow</Badge>
        <Badge color="warning">Warning</Badge>
        <Badge color="red">Red</Badge>
        <Badge color="gray">Gray</Badge>
      </div>
    </div>
  );
};

const ModalComponents = () => {
  const [isModalOpen, setIsModalOpen] = useSafeState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useSafeState(false);

  return (
    <>
      <div className="mb-12">
        <h2 id="modals" className="h2 underlined-blue mb-4">
          Modals
        </h2>
        <div className="flex-center-start gap-4">
          <button type="button" className="btn" onClick={() => setIsModalOpen(true)}>
            Simple Modal
          </button>
          <button type="button" className="btn" onClick={() => setIsCardModalOpen(true)}>
            Card Modal
          </button>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        body={<div>Simple modal without title or footer</div>}
        onClose={() => setIsModalOpen(false)}
      />

      <Modal
        isOpen={isCardModalOpen}
        body={
          <>
            <p className="font-semibold mb-4">Lorem ipsum</p>
            <p>
              Adipisicing esse eu reprehenderit cillum. Minim commodo et proident irure quis qui ut do anim. Magna ad
              officia in pariatur irure reprehenderit officia esse sint sit irure amet ad incididunt. Proident aliqua
              eiusmod aliquip occaecat nulla tempor ex aliquip irure fugiat ut commodo nostrud officia.
            </p>
          </>
        }
        header={<div className="h2 underlined-yellow mb-6">Card Modal - Lorem ipsum</div>}
        footer={
          <div className="flex-center-between mt-4">
            <button className="btn small outlined" onClick={() => setIsCardModalOpen(false)}>
              Cancel
            </button>
            <button className="btn small">Submit</button>
          </div>
        }
        onClose={() => setIsCardModalOpen(false)}
      />
    </>
  );
};

const Toasters = () => {
  const { t } = useTranslation();

  return (
    <div id="toasters" className="mb-12">
      <h2 className="h2 underlined-blue mb-4">Toasters</h2>
      <div className="flex flex-col">
        <button className="w-fit btn mb-2" onClick={() => toast.success(t('dialog:toast.success'))}>
          Afficher le toast succès
        </button>
        <button className="w-fit btn mb-2" onClick={() => toast.info(t('dialog:toast.info'))}>
          Afficher le toast info
        </button>
        <button className="w-fit btn mb-2" onClick={() => toast.warning(t('dialog:toast.warning'))}>
          Afficher le toast warning
        </button>
        <button className="w-fit btn" onClick={() => toast.error(t('dialog:toast.error'))}>
          Afficher le toast error
        </button>
      </div>
    </div>
  );
};

const TooltipComponents = () => {
  return (
    <div id="tooltips" className="mb-12">
      <h2 className="h2 underlined-blue mb-4">Tooltips</h2>

      <p className="mt-4">{docLink('React Popper', 'https://popper.js.org/react-popper/v2/')}</p>

      <div className="mt-4 flex gap-2">
        Infos en bas (défaut) <Tooltip content="Test" id="tooltip-default" />
      </div>
      <div className="mt-1 flex gap-2">
        Infos en haut <Tooltip content="Test" id="tooltip-top" placement="top" />
      </div>
      <div className="mt-1 flex gap-2">
        Infos à droite <Tooltip content="Test" id="tooltip-right" placement="right" />
      </div>
      <div className="mt-1 flex gap-2">
        Infos à gauche <Tooltip content="Test" id="tooltip-left" placement="left" />
      </div>
    </div>
  );
};

const LoadingComponents = () => {
  return (
    <div className="mb-12">
      <h2 id="loaders" className="h2 underlined-blue mb-4">
        Loaders & Skeletons
      </h2>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6">
          <h3 className="h3 mb-8">Loaders</h3>
          <div className="flex items-center">
            <Loader loaderClass="w-8 h-8 mr-10" />
            <Loader loaderClass="w-16 h-16 text-red-500 mr-10" />
            <button className="btn" disabled>
              <Loader loaderClass="w-4 h-4 text-white mr-2" />
              Load more
            </button>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <h3 className="h3 mb-4">Skeletons</h3>
          <div className="mb-8">
            <Skeleton type="text" classNames="h-8 w-40" />
            <Skeleton type="text" classNames="h-6 w-80" />
            <Skeleton type="text" />
            <Skeleton type="text" />
            <Skeleton type="text" />
            <div className="flex gap-4">
              <Skeleton type="square" classNames="w-12 h-12" />
              <Skeleton type="circle" classNames="w-12 h-12" />
            </div>
          </div>
          <Card
            title={<Skeleton type="text" classNames="h-8 w-60" />}
            cta={<Skeleton type="circle" />}
            onMobile={false}
          >
            <Skeleton type="text" />
            <Skeleton type="text" />
            <Skeleton type="text" />
          </Card>
        </div>
      </div>
    </div>
  );
};

const FormComponents = () => {
  const { t } = useTranslation();
  const selectOptions = useMemo(
    () =>
      addUuid(
        [
          { value: 'purple', label: 'Purple' },
          { value: 'blue', label: 'Blue' },
          { value: 'yellow', label: 'Yellow' },
          { value: 'red', label: 'Red' },
          { value: 'cyan', label: 'Cyan' },
          { value: 'orange', label: 'Orange' },
          { value: 'slate', label: 'Slate' },
          { value: 'silver', label: 'Silver' }
        ],
        'select-option-'
      ),
    []
  );
  const radioOptions = useMemo(
    () =>
      addUuid(
        [
          { name: 'radio', value: 'blue', label: 'Blue' },
          { name: 'radio', value: 'yellow', label: 'Yellow' },
          { name: 'radio', value: 'red', label: 'Red' }
        ],
        'input-radio-'
      ),
    []
  );

  const form = useForm({
    mode: 'all',
    defaultValues: {
      toggle: false,
      singleSelect: null,
      singleSelectIcon: selectOptions[2],
      multiSelect: [selectOptions[0]],
      firstname: '',
      lastname: '',
      radio: radioOptions[1].value,
      checkbox: false,
      textarea: null,
      datepicker: null,
      rangeDatepicker: [null, null]
    }
  });

  const onSubmit = (/* data */) => {
    if (!form.formState.isValid) return;
  };

  return (
    <div className="mb-12">
      <h2 id="forms" className="h2 underlined-blue mb-4">
        Forms
      </h2>

      <ul className="mb-12 list-disc">
        <li className="mb-3">
          {docLink('README', 'https://github.com/Kinoba/touloisirs-front/blob/staging/docs/form.md')}
        </li>
        <li className="mb-3">{docLink('react-hook-form', 'https://react-hook-form.com/get-started/')}</li>
        <li className="mb-3">{docLink('react-datepicker', 'https://reactdatepicker.com/')}</li>
        <li>{docLink('react-select', 'https://react-select.com/home')}</li>
      </ul>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="lg:grid lg:grid-cols-12 lg:gap-6">
          <div className="mb-4 lg:col-span-4">
            <FormInput
              name="firstname"
              label={t('form:firstname')}
              type="text"
              rules={{ required: false }}
              icon={{ name: 'id-badge', style: 'fad' }}
              className="mb-4"
            />

            <FormInput
              name="lastname"
              label={t('form:lastname')}
              placeholder={t('form:lastname')}
              type="text"
              rules={{ required: true }}
              className="mb-4"
            />

            <FormTextArea name="textarea" label="Textarea" placeholder="Lorem ipsum..." />
          </div>

          <div className="lg:col-span-4">
            <div className="label">Single select</div>
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  options={selectOptions}
                  onChange={onChange}
                  name="singleSelect"
                  placeholder={t('form:select.default')}
                />
              )}
              name="singleSelect"
              control={form.control}
              rules={{ required: true }}
            />

            <div className="mt-4 label">Single select with icon</div>
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  options={selectOptions}
                  onChange={onChange}
                  name="singleSelectIcon"
                  placeholder={t('form:select.default')}
                  icon={{ name: 'pen' }}
                />
              )}
              name="singleSelectIcon"
              control={form.control}
              rules={{ required: true }}
            />

            <div className="mt-4 label">Multi select</div>
            <Controller
              render={({ field: { onChange } }) => (
                <FormSelect
                  options={selectOptions}
                  onChange={onChange}
                  name="multiSelect"
                  placeholder={t('form:select.default')}
                  isMulti
                />
              )}
              name="multiSelect"
              control={form.control}
            />

            <div className="mt-4">
              <Controller
                control={form.control}
                name="datepicker"
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    selected={value}
                    onChange={onChange}
                    name="datepicker"
                    label="Date picker"
                    icon={{ name: 'pen' }}
                    placeholder="jj/mm/aaaa"
                    minDate={new Date()}
                  />
                )}
              />
            </div>

            <div className="mt-4">
              <Controller
                control={form.control}
                name="rangeDatepicker"
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    onChange={onChange}
                    name="rangeDatepicker"
                    label="Range Date picker"
                    placeholder="jj/mm/aaaa"
                    selectsRange
                    startDate={value[0]}
                    endDate={value[1]}
                    formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
                  />
                )}
              />
            </div>
          </div>

          <div className="mb-4 lg:col-span-4">
            <FormCheckbox
              name="checkbox"
              labelI18nKey="form:termsLabel"
              link={{ url: '', labelI18nKey: 'form:terms', target: '_blank' }}
              rules={{ required: true }}
              className="mb-8 mt-4 lg:mt-0"
            />

            <FormToggle
              name="toggle"
              labeli18nKey="form:termsLabel"
              link={{ url: '', labelI18nKey: 'form:terms', target: '_blank' }}
              rules={{ required: false }}
              className="mb-8"
            />

            <RadioGroup radioOptions={radioOptions} groupLabel={t('form:select.default')} />
          </div>

          <button type="submit" className="btn small">
            Submit
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

const Table = () => {
  const columns = [
    {
      accessorKey: 'name',
      header: 'Nom',
      enableSorting: false,
      cell: (name) => <span className="text-primary-500 font-normal">{name.getValue()}</span>
    },
    { accessorKey: 'age', header: 'Âge' },
    { accessorKey: 'city', header: 'Ville' }
  ];

  const tableData = [
    { name: 'You', age: 29, city: 'Lyon' },
    { name: 'agent 007', age: 60, city: 'Paris' },
    { name: 'Darth Vader', age: 40, city: 'Lyon' }
  ];

  return (
    <div className="mb-12">
      <h2 id="table" className="h2 underlined-blue mb-4">
        Table
      </h2>

      <div className="mb-6">{docLink('TanStack ReactTable', 'https://tanstack.com/table/v8/docs/guide/overview')}</div>

      <ReactTable columns={columns} data={tableData} />
    </div>
  );
};

const TabsComponents = () => {
  const tabs = [
    { identifier: 'tab1', label: 'Tab 1' },
    { identifier: 'tab2', label: 'Tab 2' },
    { identifier: 'tab3', label: 'Tab 3' }
  ];

  const tabsResponsive = [
    { identifier: 'tab1Responsive', label: 'Hébergement', icon: { name: 'plus' } },
    { identifier: 'tab2Responsive', label: 'Restauration', icon: { name: 'plus' } },
    { identifier: 'tab3Responsive', label: 'Services', icon: { name: 'plus' } },
    { identifier: 'tab4Responsive', label: 'Bon à savoir', icon: { name: 'plus' } }
  ];

  return (
    <div id="tabs" className="mb-12">
      <h2 className="h2 underlined-blue mb-4">Tabs</h2>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5">
          <TabsProvider defaultTab={tabs[1].identifier}>
            <div className="rounded-lg shadow-md shadow-gray-500/25 p-4 pb-0">
              <Tabs tabs={tabs} />
            </div>

            <div className="rounded-lg shadow-md shadow-gray-500/25 p-4 mt-6">
              <TabPanel identifier={tabs[0].identifier}>Tab 1 - Lorem ipsum dolor sit amet.</TabPanel>
              <TabPanel identifier={tabs[1].identifier}>
                Tab 2 - Ad alias dolore eos hic provident, quam quia tempore veritatis voluptatum.
              </TabPanel>
              <TabPanel identifier={tabs[2].identifier}>
                Tab 3 - Aspernatur ducimus pariatur quo saepe sed, velit!
              </TabPanel>
            </div>
          </TabsProvider>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <TabsProvider defaultTab={tabsResponsive[1].identifier}>
            <Tabs tabs={tabsResponsive} type="card" />

            <div className="mt-4 md:mt-12">
              <TabPanel identifier={tabsResponsive[0].identifier}>
                Tab 1 - Ex cillum consequat est occaecat sint in ullamco dolor ex aliquip ut esse consectetur anim..
              </TabPanel>
              <TabPanel identifier={tabsResponsive[1].identifier}>
                Tab 2 - Proident do reprehenderit culpa ut fugiat sint aliquip labore quis officia do officia dolore
                quis.
              </TabPanel>
              <TabPanel identifier={tabsResponsive[2].identifier}>
                Tab 3 - Occaecat proident cillum dolore incididunt ullamco minim dolor fugiat dolor sunt exercitation
                culpa in in.
              </TabPanel>
              <TabPanel identifier={tabsResponsive[3].identifier}>
                Tab 4 - Reprehenderit elit sint consectetur exercitation anim occaecat dolore in ut pariatur dolor
                fugiat cupidatat.
              </TabPanel>
            </div>
          </TabsProvider>
        </div>
      </div>
    </div>
  );
};

const AccordionComponents = () => {
  const accordion = [
    {
      title: 'Accordion 1 - Lorem enim non ea adipisicing',
      /* eslint-disable */
      content:
        'Lorem quis labore qui ea consequat adipisicing mollit ex amet sunt ex cillum cupidatat ea. Lorem quis labore qui ea consequat adipisicing mollit ex amet sunt ex cillum cupidatat ea. Lorem quis labore qui ea consequat adipisicing mollit ex amet sunt ex cillum cupidatat ea'
      /* eslint-enable */
    },
    {
      title: 'Accordion 2 - Labore commodo velit laborum',
      content: 'Commodo elit ex laborum ullamco ea cillum commodo id adipisicing in laboris occaecat.'
    },
    {
      title: 'Accordion 3 - Ea sunt sit ea exercitation',
      content: 'Proident proident ipsum quis. Deserunt qui sint eu.'
    }
  ];

  return (
    <div id="accordions" className="mb-12">
      <h2 className="h2 underlined-blue mb-4">Accordions</h2>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6">
          <h3 className="h3 underlined-blue mb-6">Single opened accordion</h3>

          <AccordionProvider defaultOpen={[0]}>
            {accordion.map((item, i) => (
              <Accordion key={`accordion-single-${i}`} itemIndex={i}>
                <AccordionTitle icon={{ name: 'angle-down', class: 'accordion-arrow' }}>
                  <span>{item.title}</span>
                </AccordionTitle>

                <AccordionContent>{item.content}</AccordionContent>
              </Accordion>
            ))}
          </AccordionProvider>
        </div>

        <div className="col-span-12 lg:col-span-6">
          <h3 className="h3 underlined-blue mb-6">Multiple opened accordion</h3>

          <AccordionProvider allowMultipleOpen>
            <div className="rounded-lg shadow-md shadow-gray-500/25 p-4">
              {accordion.map((item, i) => (
                <Accordion key={`accordion-multiple-${i}`} itemIndex={i} className="card">
                  <AccordionTitle icon={{ name: 'plus' }}>{item.title}</AccordionTitle>
                  <AccordionContent>{item.content}</AccordionContent>
                </Accordion>
              ))}
            </div>
          </AccordionProvider>
        </div>
      </div>
    </div>
  );
};

const ScrollsComponents = () => {
  /* Placeholder methods for InfiniteScrolls */
  const loadFiveMore = async (items) => {
    await new Promise((res) => setTimeout(res, 1500)); // just a way to wait 1.5 seconds for demonstration purposes
    return items.length < 15 ? [...items, uniqueId(), uniqueId(), uniqueId(), uniqueId(), uniqueId()] : items;
  };

  const loadAlwaysFiveMore = async (items) => {
    await new Promise((res) => setTimeout(res, 1500)); // just a way to wait 1.5 seconds for demonstration purposes
    return [...items, uniqueId(), uniqueId(), uniqueId(), uniqueId(), uniqueId()];
  };

  return (
    <div className="mb-12">
      <h2 id="scrolls" className="h2 underlined-blue mb-4">
        Scrolls
      </h2>
      {docLink('README', 'https://github.com/Kinoba/touloisirs-front/blob/staging/docs/infinite-scroll.md')}
      {docLink('react-infinite-scroll-component', 'https://github.com/ankeetmaini/react-infinite-scroll-component')}

      <h3 className="h3 mt-8 mb-4">Infinite scroll with parent scroll</h3>
      <div id="infiniteScroll" className="content" style={{ height: 100, overflow: 'auto' }}>
        <InfiniteScroll
          items={[uniqueId(), uniqueId(), uniqueId(), uniqueId(), uniqueId()]}
          loadMore={loadAlwaysFiveMore}
          options={{
            displayItem: (item, index) => <li key={uniqueId()}>item - {index}</li>,
            scrollableTarget: 'infiniteScroll'
          }}
        />
      </div>

      <h3 className="h3 my-6">Finite scroll (15 items) with parent scroll</h3>
      <div id="finiteScroll" className="content" style={{ height: 100, overflow: 'auto' }}>
        <InfiniteScroll
          items={[uniqueId(), uniqueId(), uniqueId(), uniqueId(), uniqueId()]}
          loadMore={loadFiveMore}
          options={{
            displayItem: (item, index) => <li key={uniqueId()}>item - {index}</li>,
            scrollableTarget: 'finiteScroll'
          }}
        />
      </div>
      <h3 className="h3 my-6">Finite scroll (15 items) with window scroll</h3>
      <div className="content">
        <InfiniteScroll
          items={[uniqueId(), uniqueId(), uniqueId(), uniqueId(), uniqueId()]}
          loadMore={loadFiveMore}
          options={{
            displayItem: (item, index) => <li key={uniqueId()}>item - {index}</li>
          }}
        />
      </div>
    </div>
  );
};

const Cards = () => {
  return (
    <div className="mb-12">
      <h2 id="cards" className="h2 underlined-blue mb-6">
        Cards
      </h2>

      <Card
        title={<h2 className="h2 underlined-yellow">Card with title and CTA</h2>}
        cta={<button>Modifier</button>}
        onMobile={false}
      >
        This card looses its styles on small devices and the content seems displayed on the page
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8 mt-8">
        <Card title={<h2 className="h2 underlined-yellow">Card with title</h2>}>Lorem ipsum</Card>

        <Card
          title={<h2 className="h3 text-primary-500">Card with a different title</h2>}
          type="colored"
          cta={
            <Tooltip
              content="Lorem ipsum dolor sit amet consectetur. Vitae porta semper et in donec varius"
              id="card-tooltip"
            />
          }
        >
          Lorem ipsum
        </Card>

        <Card>Card without title nor CTA</Card>
      </div>
    </div>
  );
};

const Sliders = () => {
  const images = [
    { src: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470', alt: 'Lac de montagne' },
    { src: 'https://images.unsplash.com/photo-1494935362342-566c6d6e75b5', alt: 'Coucher de soleil' },
    { src: 'https://images.unsplash.com/photo-1690184432588-81068877d852', alt: 'Lac de montagne en hiver' }
  ];

  return (
    <div className="mb-12">
      <h2 id="sliders" className="h2 underlined-blue mb-4">
        Sliders
      </h2>

      <p className="mb-4">
        {docLink('React Splide', 'https://splidejs.com/integration/react-splide/')}
        {docLink('Splide options', 'https://splidejs.com/guides/options/')}
      </p>

      <Slider
        type="loop"
        options={{ perPage: 3, perMove: 1, gap: '1.5rem', padding: { right: '10%' } }}
        sliderClass="with-card-shadow"
      >
        {images.map((image, i) => (
          <Slide slideClass="rounded-lg overflow-hidden shadow-lg" key={`slider-1-${i}`}>
            <img {...image} />
            <div className="p-6">
              <h3 className="h2 underlined-yellow mb-4">Card</h3>
              <p>Slide {i + 1}</p>
            </div>
          </Slide>
        ))}
      </Slider>

      <Slider type="fade" sliderClass="absolute-arrows mt-4">
        <Slide image={images[0]}>
          <button className="btn">Slide 1</button>
        </Slide>
        <Slide image={images[1]}>
          <p className="text-white">Slide 2 avec du texte au lieu d&apos;un bouton</p>
        </Slide>
      </Slider>

      <Slider type="loop" options={{ perPage: 2, perMove: 1, gap: '1rem' }} sliderClass="absolute-arrows mt-4">
        {images.map((image, i) => (
          <Slide image={image} key={`slider-1-${i}`} />
        ))}
      </Slider>

      <Slider type="fade" sliderClass="mt-4">
        <Slide>
          <p className="text-center">Slide 1 (slider avec du texte seulement)</p>
        </Slide>
        <Slide>
          <p className="text-center">Slide 2</p>
        </Slide>
      </Slider>
    </div>
  );
};

const FooterSection = () => {
  const [withBanner, setWithBanner] = useSafeState(false);
  const dispatch = useDispatch();

  const { isLoggedIn } = useUser();
  const initialBannerData = {
    title: 'Besoin d’aide ?',
    subTitle: 'Besoin d’aide ou d’un conseil ? Une question ?',
    description:
      // eslint-disable-next-line max-len
      'Nos conseillers vous accompagnent tout au long de votre démarche pour vous fournir une réponse adaptée et personnalisée.',
    linkText: 'Consulter notre centre d’aide',
    linkTarget: '#help',
    imageUrl: 'http://localhost:5100/footer-banner-image.png'
  };
  const handleBanner = () => {
    if (withBanner) dispatch(resetFooterBanner());
    else dispatch(setFooterBanner(initialBannerData));
    setWithBanner(!withBanner);
  };

  return (
    <div id="footer" className="w-full mb-12">
      <h2 className="h2 underlined-blue mb-6">Footer</h2>
      <div className="flex gap-4 mb-4">
        <button className="btn w-fit">Voir la version {isLoggedIn ? 'déconnecté' : 'connecté'}</button>
        <button className="btn w-fit" onClick={handleBanner}>
          Voir la version {withBanner ? 'sans' : 'avec'} la bannière d&apos;aide
        </button>
      </div>
      <Footer />
    </div>
  );
};

const DesignSystemPage = () => {
  return (
    <div className="mt-20">
      <h1 className="h1 my-6 ml-6 lg:mb-1">Design System</h1>
      <div className="p-6 lg:grid lg:grid-cols-12">
        <div className="mb-12 lg:col-span-2 2xl:col-span-3">
          <div className="lg:sticky lg:top-4">{SkipLinks()}</div>
        </div>
        <div className="lg:col-span-10 2xl:col-span-9 2xl:pr-12">
          <AccordionComponents />
          <Badges />
          <BannerComponents />
          <Buttons />
          <Cards />
          <Colors />
          <FooterSection />
          <FormComponents />
          <Icons />
          <LoadingComponents />
          <ModalComponents />
          <ScrollsComponents />
          <Sliders />
          <StepperComponent />
          <TabsComponents />
          <Table />
          <Toasters />
          <TooltipComponents />
          <Typography />
        </div>
      </div>
    </div>
  );
};

export default DesignSystemPage;
